import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailPresenceUser } from '../hooks/useEmailPresence';

interface EmailPresenceIndicatorProps {
  users: EmailPresenceUser[];
  className?: string;
}

const EmailPresenceIndicator: React.FC<EmailPresenceIndicatorProps> = ({ users, className }) => {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const others = users.filter((u) => u.userId !== user?.id);

  if (others.length === 0) return null;

  const label = others
    .map((u) =>
      u.activity === 'replying'
        ? t('presenceReplying', { name: u.name })
        : t('presenceViewing', { name: u.name }),
    )
    .join(', ');

  return (
    <div
      className={cn(
        'ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
        className,
      )}
      title={label}
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-600" />
      </span>
      <span className={cn('truncate max-w-[220px]', colors.text)}>{label}</span>
    </div>
  );
};

export default EmailPresenceIndicator;
